/**
 * 「答えられる商品」の絞り込み。商品一覧と売り方の提案の商品の候補で使う。
 *
 * **既定は「絞らない」**。答えられるかどうかは、ペルソナ別の充足の集計が商品ごとに
 * 残した数で決まるので、集計していない商品（集計の対象の上限 1,000 件に入らなかった
 * 商品を含む）は絞り込むと出なくなる。絞り込んでいる間はそのことを注記する。
 */

import { useQuery } from "urql";
import {
  PersonasDocument,
  type AnswerableBasis,
  type AnswerableProductFilter,
} from "visumo-data-agent-web-shared";

/** 絞らない（空文字）か、答えられるの基準。 */
export type AnswerableChoice = "" | AnswerableBasis;

export const ANSWERABLE_CHOICE_LABEL: Record<AnswerableChoice, string> = {
  "": "絞らない",
  ANY_COVERED: "答えられる商品（判定で答えられる問いが 1 件以上）",
  FAQ_COVERED: "FAQ で答えられる商品",
};

const CHOICES: readonly AnswerableChoice[] = ["", "ANY_COVERED", "FAQ_COVERED"];

export const ANSWERABLE_SCOPE_NOTE =
  "集計で判定した商品（売り方の提案の候補を優先した上限 1,000 件）だけが対象です";

/** 画面の選択を API の絞り込みへ写す。絞らないときは null（送らない）。 */
export function toAnswerableFilter(
  choice: AnswerableChoice,
  personaId: string,
): AnswerableProductFilter | null {
  if (choice === "") return null;
  return {
    basis: choice,
    ...(personaId === "" ? {} : { personaId }),
  };
}

export function AnswerableFilterFields({
  choice,
  personaId,
  onChange,
  idPrefix,
}: {
  choice: AnswerableChoice;
  personaId: string;
  onChange: (choice: AnswerableChoice, personaId: string) => void;
  /** 同じ画面に 2 つ置いても label と select の対応がずれないように分ける */
  idPrefix: string;
}) {
  const [personas] = useQuery({ query: PersonasDocument });
  const personaOptions = personas.data?.personas ?? [];

  return (
    <div className="vd-stack" style={{ gap: 4 }}>
      <span className="vd-row" style={{ alignItems: "flex-end" }}>
        <label className="vd-field" htmlFor={`${idPrefix}-answerable`}>
          <span className="vd-field-hint">答えられる商品</span>
          <select
            id={`${idPrefix}-answerable`}
            className="vd-select"
            value={choice}
            onChange={(event) =>
              onChange(event.target.value as AnswerableChoice, personaId)
            }
          >
            {CHOICES.map((value) => (
              <option key={value} value={value}>
                {ANSWERABLE_CHOICE_LABEL[value]}
              </option>
            ))}
          </select>
        </label>
        <label className="vd-field" htmlFor={`${idPrefix}-answerable-persona`}>
          <span className="vd-field-hint">ペルソナ</span>
          <select
            id={`${idPrefix}-answerable-persona`}
            className="vd-select"
            value={personaId}
            // 絞らないときにペルソナを選んでも何も変わらない。押せない形で置く
            disabled={choice === ""}
            onChange={(event) => onChange(choice, event.target.value)}
          >
            <option value="">すべてのペルソナ</option>
            {personaOptions.map((persona) => (
              <option key={persona.id} value={persona.id}>
                {persona.name}
              </option>
            ))}
          </select>
        </label>
      </span>
      {choice === "" ? null : (
        <p className="vd-stat-note" style={{ margin: 0 }}>
          {ANSWERABLE_SCOPE_NOTE}
        </p>
      )}
    </div>
  );
}
